/**
 * PaywallGate — intercepts 402 responses globally and shows an upgrade modal.
 *
 * Mounted once in DashboardLayout. When any API call returns 402
 * (subscription_required), we show a full-screen overlay prompting the
 * user to subscribe. The overlay lets them navigate to billing settings.
 *
 * Also provides a usePaywall() hook for components that want to show
 * inline upgrade prompts without waiting for a 402.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMe, useSubscription } from '@/lib/hooks/use-me';
import { useBasePath } from '@/lib/config/nav-helpers';
import { standaloneHome } from '@/lib/platform-pin';
import { ApiError } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { formatPrice, planFor, trialTerms } from '@/lib/billing';

interface PaywallState {
  blocked: boolean;
  reason: string | null;
  message: string | null;
  dismiss: () => void;
}

const PaywallContext = createContext<PaywallState>({
  blocked: false,
  reason: null,
  message: null,
  dismiss: () => {},
});

export function usePaywall() {
  return useContext(PaywallContext);
}

export function PaywallProvider({ children }: { children: React.ReactNode }) {
  const [blocked, setBlocked] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const sub = useSubscription();

  const dismiss = useCallback(() => setBlocked(false), []);

  // Listen for 402 errors from React Query's global error handler
  const queryClient = useQueryClient();

  useEffect(() => {
    const defaults = queryClient.getDefaultOptions();

    queryClient.setDefaultOptions({
      queries: {
        ...defaults.queries,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status === 402) return false;
          return failureCount < 2;
        },
      },
      mutations: {
        ...defaults.mutations,
        onError: (error) => {
          if (error instanceof ApiError && error.status === 402) {
            const body = error.body as Record<string, unknown> | null;
            setReason((body?.reason as string) ?? 'subscription_required');
            setMessage((body?.message as string) ?? 'Please subscribe to continue.');
            setBlocked(true);
          }
        },
      },
    });
  }, [queryClient]);

  // Also detect from query errors
  useEffect(() => {
    const cache = queryClient.getQueryCache();
    const unsub = cache.subscribe((event) => {
      if (event.type === 'updated' && event.query.state.error) {
        const err = event.query.state.error;
        if (err instanceof ApiError && err.status === 402) {
          const body = err.body as Record<string, unknown> | null;
          setReason((body?.reason as string) ?? 'subscription_required');
          setMessage((body?.message as string) ?? 'Please subscribe to continue.');
          setBlocked(true);
        }
      }
    });
    return unsub;
  }, [queryClient]);

  // Auto-detect from /v1/me data. 'incomplete' = a new workspace that hasn't
  // started its card trial at Checkout yet (apps/api subscription-gate.ts).
  useEffect(() => {
    if (!sub.isActive && sub.trialEndsAt) {
      const r = sub.status === 'past_due' ? 'payment_failed'
        : sub.status === 'incomplete' ? 'trial_not_started'
        : 'trial_expired';
      setReason(r);
      setMessage(
        r === 'payment_failed'
          ? 'Your payment failed. Update your payment method to restore access.'
          : r === 'trial_not_started'
          ? 'Pick a plan to start your 14-day free trial.'
          : 'Your free trial has ended. Subscribe to keep using Waypoint.'
      );
      setBlocked(true);
    }
  }, [sub.isActive, sub.trialEndsAt, sub.status]);

  return (
    <PaywallContext.Provider value={{ blocked, reason, message, dismiss }}>
      {children}
      {blocked && <PaywallOverlay reason={reason} message={message} onDismiss={dismiss} />}
    </PaywallContext.Provider>
  );
}

function PaywallOverlay({
  reason,
  message,
  onDismiss,
}: {
  reason: string | null;
  message: string | null;
  onDismiss: () => void;
}) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const sub = useSubscription();
  // A standalone product workspace has no /dashboard: its Billing tab lives in
  // the workspace settings mounted inside its own shell (docs/PRODUCT_LINES.md).
  const me = useMe();
  const home = standaloneHome({ workspaceKind: me.workspaceKind, homePath: me.homePath });
  const billingPath = home ? `${home}/settings/workspace?tab=billing` : `${basePath}/settings?tab=billing`;

  const notStarted = reason === 'trial_not_started';
  const title = reason === 'payment_failed'
    ? 'Payment Failed'
    : reason === 'subscription_canceled'
    ? 'Subscription Canceled'
    : notStarted
    ? 'Start your free trial'
    : 'Free Trial Ended';

  const icon = reason === 'payment_failed' ? '💳' : notStarted ? '🚀' : '⏰';

  const features = [
    'AI-powered deal coaching & risk alerts',
    'Revenue forecasting & cash runway',
    'Meeting intelligence & promise tracking',
    'Contract analysis & auto-renewal alerts',
    'Cross-signal convergence detection',
    'Morning briefings & weekly digests',
  ];

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/60 backdrop-blur-xs animate-fade-in">
      <div className="w-full max-w-lg mx-4 rounded-2xl border border-border bg-surface-0 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-linear-to-r from-primary/10 to-primary/5 px-8 py-6 text-center">
          <span className="text-4xl">{icon}</span>
          <h2 className="mt-3 text-lg font-bold text-ink">{title}</h2>
          <p className="mt-2 text-sm text-ink-dim max-w-md mx-auto">
            {message}
          </p>
        </div>

        {/* What you're missing */}
        <div className="px-8 py-6 space-y-4">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
            What you get with Waypoint
          </p>
          <div className="grid grid-cols-1 gap-2">
            {features.map((f) => (
              <div key={f} className="flex items-start gap-2">
                <svg className="w-4 h-4 text-primary shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-xs text-ink">{f}</span>
              </div>
            ))}
          </div>

          {/* Pricing hint */}
          <div className="rounded-lg bg-surface-1 border border-border px-4 py-3 text-center">
            <p className="text-xs text-ink">
              <span className="text-ink-dim">Owner or Sales </span>
              <span className="font-bold text-primary">{formatPrice(planFor('owner').monthlyCents)}/mo</span>
              <span className="text-ink-dim"> · both as Solo </span>
              <span className="font-bold text-primary">{formatPrice(planFor('bundle').monthlyCents)}/mo</span>
            </p>
            {notStarted && (
              <p className="mt-1 text-2xs text-ink-dim">{trialTerms()}</p>
            )}
            {!notStarted && sub.trialDaysRemaining === 0 && (
              <p className="mt-1 text-2xs text-ink-dim">
                Your data is safe — subscribe anytime to pick up where you left off.
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-border px-8 py-5 flex items-center justify-between gap-3">
          <button
            onClick={onDismiss}
            className="text-2xs text-ink-dim hover:text-ink transition-colors"
          >
            Browse (read-only)
          </button>
          <button
            onClick={() => {
              onDismiss();
              navigate(billingPath);
            }}
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
          >
            {reason === 'payment_failed' ? 'Update Payment' : notStarted ? 'Choose a plan' : 'Subscribe Now'}
          </button>
        </div>
      </div>
    </div>
  );
}
